import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { ASSETS, BRAND_TAGLINE } from "@/data/site";

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const TarotSection = () => {
  return (
    <section id="tarot" className="relative section starfield tone-tarot" data-testid="tarot-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-cyan)" }}>Learn the cards</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight"
            data-testid="tarot-heading"
            style={{ color: "var(--cs-cyan)" }}
          >
            A guide to the <span className="italic">tarot</span>
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative lg:sticky lg:top-28"
          >
            <div className="absolute -inset-6 rounded-3xl blur-3xl opacity-35"
                 style={{ background: "radial-gradient(circle, rgba(98,195,216,0.45), transparent 70%)" }} />
            <div className="relative rounded-3xl overflow-hidden border border-white/15 shadow-2xl">
              <img
                src={ASSETS.riderWaitePack}
                alt="Original Rider-Waite Tarot Pack with deck"
                className="w-full h-auto object-cover"
                data-testid="tarot-image"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="space-y-6"
          >
            <div className="glass rounded-3xl p-8 md:p-10" data-testid="tarot-history-box">
              <h3 className="font-serif-display text-2xl md:text-3xl mb-4" style={{ color: "var(--cs-cyan)" }}>
                Centuries of Wisdom
              </h3>
              <p className="text-slate-100 leading-relaxed mb-4">
                Tarot cards have been around for centuries, used for divination and fortune telling. The tarot cards are rich in meaning and symbolism, guiding the reader (querent) through the themes, progress, and obstacles of life's journey.
              </p>
              <p className="text-slate-100 leading-relaxed">
                There are <span className="font-medium" style={{ color: "var(--cs-cyan)" }}>78 cards</span> in each tarot deck. <span className="font-medium" style={{ color: "var(--cs-cyan)" }}>22 Major Arcana</span>, which stand for significant life situations and transitions. The remaining <span className="font-medium" style={{ color: "var(--cs-cyan)" }}>56 cards</span> are divided among four suits of 14 — 10 minor cards and 4 court cards. These are <em>wands</em> (fire, dreams and aspirations), <em>cups</em> (water, flow and emotions), <em>swords</em> (air, thinking and the mind), and <em>pentacles</em> (earth, money and material things).
              </p>
            </div>

            <div className="glass rounded-3xl p-8 md:p-10" data-testid="tarot-benefits-box">
              <h3 className="font-serif-display text-2xl md:text-3xl mb-4" style={{ color: "var(--cs-cyan)" }}>
                Benefits of Learning Tarot
              </h3>
              <p className="text-slate-100 leading-relaxed mb-3">
                If you are interested in spiritual or metaphysical matters — or simply want to develop your intuition and wisdom that can guide you through life's challenges and opportunities — tarot cards are rich in meaning, imagery, and symbolism representing different aspects of life: emotions, finances, challenges, and passions.
              </p>
              <p className="text-slate-100 leading-relaxed mb-3">
                Learning to read tarot cards is a powerful tool for self-reflection and personal growth. Rather than predicting the future, tarot invites you to slow down, tune into your intuition, and explore the deeper currents of your thoughts and emotions.
              </p>
              <p className="text-slate-100 leading-relaxed">
                Whether you're navigating uncertainty or simply seeking clarity, tarot provides a meaningful framework for understanding yourself and the world around you.
              </p>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              <button onClick={scrollTo("deck")} className="btn-pill-blue" data-testid="tarot-cta-deck">
                Explore the complete deck
              </button>
              <button onClick={scrollTo("contact")} className="btn-pill-primary" data-testid="tarot-cta-book">
                <Sparkles className="w-4 h-4" /> Book a reading
              </button>
            </div>
          </motion.div>
        </div>

        {/* Standalone brand tagline — its own dark-blue box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 max-w-3xl mx-auto box-dark px-8 py-10 md:px-12 md:py-12 text-center"
          style={{ boxShadow: "0 18px 50px rgba(15,20,55,0.55)" }}
          data-testid="tarot-tagline"
        >
          <p className="brand-tagline text-xl md:text-2xl leading-relaxed text-white">
            "{BRAND_TAGLINE}"
          </p>
          <p className="mt-3 text-sm tracking-widest uppercase" style={{ color: "var(--cs-cyan)" }}>— Verna</p>
        </motion.div>
      </div>
    </section>
  );
};

export default TarotSection;
